import { useState, useEffect } from "react";
import axios from "axios";
import { useLanguage } from "@/contexts/LanguageContext";
import { formatCurrency } from "@/lib/currencies";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  LineChart,
  Line
} from "recharts";
import { 
  TrendingUp, 
  TrendingDown, 
  Receipt, 
  Calendar, 
  Bell, 
  AlertTriangle,
  Euro,
  CheckCircle2,
  Clock
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CHART_COLORS = ["#E31E24", "#09090b", "#71717a", "#d4d4d8", "#a1a1aa"];

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const { t } = useLanguage();

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/stats`);
      setStats(response.data);
    } catch (error) {
      console.error("Failed to fetch stats:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[...Array(4)].map((_, i) => (
            <Card key={i} className="animate-pulse">
              <CardContent className="p-6">
                <div className="h-20 bg-muted rounded" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  const pieData = stats?.category_breakdown?.slice(0, 5).map((cat, index) => ({
    name: cat.category_name,
    value: cat.amount,
    color: CHART_COLORS[index % CHART_COLORS.length]
  })) || [];

  return (
    <div className="space-y-6 md:space-y-8" data-testid="dashboard">
      {/* Header */}
      <div className="animate-fadeIn">
        <h1 className="text-2xl md:text-3xl font-bold font-[Manrope] tracking-tight">{t("dashboard.title")}</h1>
        <p className="text-muted-foreground mt-1 text-sm md:text-base">
          {t("common.expenses")} - {t("dashboard.thisYear")}
        </p>
      </div>

      {/* Stats Cards - Bento Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6">
        {/* Monthly Total */}
        <Card className="card-hover animate-fadeIn stagger-1" data-testid="stat-month-total">
          <CardContent className="p-3 md:p-6">
            <div className="flex items-start justify-between">
              <div className="min-w-0 flex-1">
                <p className="text-[10px] md:text-sm font-medium text-muted-foreground">{t("dashboard.thisMonth")}</p>
                <p className="text-sm sm:text-lg md:text-3xl font-bold mt-1 md:mt-2 font-[JetBrains_Mono]">
                  {formatCurrency(stats?.month_total || 0)}
                </p>
              </div>
              <div className="hidden sm:flex h-8 w-8 md:h-12 md:w-12 rounded-full bg-[#E31E24]/10 items-center justify-center flex-shrink-0 ml-2">
                <Euro className="h-4 w-4 md:h-6 md:w-6 text-[#E31E24]" />
              </div>
            </div>
            <div className="mt-1 md:mt-4 flex items-center text-[10px] md:text-sm">
              <span className="text-muted-foreground">
                {stats?.expense_count || 0} {t("common.expenses")}
              </span>
            </div>
          </CardContent>
        </Card>

        {/* Year Total */}
        <Card className="card-hover animate-fadeIn stagger-2" data-testid="stat-year-total">
          <CardContent className="p-3 md:p-6">
            <div className="flex items-start justify-between">
              <div className="min-w-0 flex-1">
                <p className="text-[10px] md:text-sm font-medium text-muted-foreground">{t("dashboard.thisYear")}</p>
                <p className="text-sm sm:text-lg md:text-3xl font-bold mt-1 md:mt-2 font-[JetBrains_Mono]">
                  {formatCurrency(stats?.year_total || 0)}
                </p>
              </div>
              <div className="hidden sm:flex h-8 w-8 md:h-12 md:w-12 rounded-full bg-[#09090b]/10 items-center justify-center flex-shrink-0 ml-2">
                <Calendar className="h-4 w-4 md:h-6 md:w-6 text-[#09090b]" />
              </div>
            </div>
            <div className="mt-1 md:mt-4 flex items-center text-[10px] md:text-sm">
              <span className="text-[#E31E24]">{t("dashboard.fixCosts")}: {formatCurrency(stats?.fix_total || 0)}</span>
            </div>
          </CardContent>
        </Card>

        {/* Pending Reminders */}
        <Card className="card-hover animate-fadeIn stagger-3" data-testid="stat-reminders">
          <CardContent className="p-3 md:p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-[10px] md:text-sm font-medium text-muted-foreground">{t("dashboard.reminders")}</p>
                <p className="text-sm sm:text-lg md:text-3xl font-bold mt-1 md:mt-2">
                  {stats?.pending_reminders || 0}
                </p>
              </div>
              <div className="hidden sm:flex h-8 w-8 md:h-12 md:w-12 rounded-full bg-amber-500/10 items-center justify-center flex-shrink-0 ml-2">
                <Bell className="h-4 w-4 md:h-6 md:w-6 text-amber-500" />
              </div>
            </div>
            <div className="mt-1 md:mt-4 flex items-center text-[10px] md:text-sm text-muted-foreground">
              {t("reminders.dueDate")} - 7 {t("common.date")}
            </div>
          </CardContent>
        </Card>

        {/* Cancellations */}
        <Card className="card-hover animate-fadeIn stagger-4" data-testid="stat-cancellations">
          <CardContent className="p-3 md:p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-[10px] md:text-sm font-medium text-muted-foreground">{t("reminders.cancellation")}</p>
                <p className="text-sm sm:text-lg md:text-3xl font-bold mt-1 md:mt-2">
                  {stats?.upcoming_cancellations || 0}
                </p>
              </div>
              <div className="hidden sm:flex h-8 w-8 md:h-12 md:w-12 rounded-full bg-red-500/10 items-center justify-center flex-shrink-0 ml-2">
                <AlertTriangle className="h-4 w-4 md:h-6 md:w-6 text-red-500" />
              </div>
            </div>
            <div className="mt-1 md:mt-4 flex items-center text-[10px] md:text-sm text-muted-foreground">
              {t("reminders.dueDate")} - 30 {t("common.date")}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
        {/* Monthly Trend Chart */}
        <Card className="lg:col-span-2 animate-fadeIn stagger-5" data-testid="chart-trend">
          <CardHeader className="p-4 md:p-6">
            <CardTitle className="font-[Manrope] text-base md:text-lg">{t("dashboard.monthlyTrend")}</CardTitle>
          </CardHeader>
          <CardContent className="p-2 md:p-6 pt-0">
            <div className="h-[200px] md:h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={stats?.monthly_trend || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                  <XAxis 
                    dataKey="month_name" 
                    tick={{ fontSize: 10 }}
                    tickLine={false}
                    interval="preserveStartEnd"
                  />
                  <YAxis 
                    tick={{ fontSize: 10 }}
                    tickLine={false}
                    tickFormatter={(value) => `${(value / 1000).toFixed(0)}k`}
                    width={35}
                  />
                  <Tooltip 
                    formatter={(value) => formatCurrency(value)}
                    contentStyle={{ 
                      backgroundColor: "white", 
                      border: "1px solid #e4e4e7",
                      borderRadius: "8px",
                      fontSize: "12px"
                    }}
                  />
                  <Bar dataKey="fix" name={t("dashboard.fixCosts")} fill="#E31E24" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="variable" name={t("dashboard.variableCosts")} fill="#09090b" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Category Breakdown */}
        <Card className="animate-fadeIn stagger-5" data-testid="chart-categories">
          <CardHeader className="p-4 md:p-6">
            <CardTitle className="font-[Manrope] text-base md:text-lg">{t("dashboard.byCategory")}</CardTitle>
          </CardHeader>
          <CardContent className="p-2 md:p-6 pt-0">
            <div className="h-[200px] md:h-[300px]">
              {pieData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={90}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {pieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip 
                      formatter={(value) => formatCurrency(value)}
                      contentStyle={{ 
                        backgroundColor: "white", 
                        border: "1px solid #e4e4e7",
                        borderRadius: "8px"
                      }}
                    />
                    <Legend 
                      verticalAlign="bottom" 
                      height={36}
                      formatter={(value) => <span className="text-sm">{value}</span>}
                    />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-muted-foreground">
                  {t("dashboard.noExpenses")}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Fix vs Variable + Payment Status */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
        <Card className="animate-fadeIn" data-testid="stat-fix-variable">
          <CardHeader className="p-4 md:p-6">
            <CardTitle className="font-[Manrope] text-base md:text-lg">{t("dashboard.fixCosts")} vs. {t("dashboard.variableCosts")}</CardTitle>
          </CardHeader>
          <CardContent className="p-4 md:p-6 pt-0">
            <div className="space-y-4">
              <div>
                <div className="flex justify-between mb-2">
                  <span className="text-xs md:text-sm font-medium">{t("dashboard.fixCosts")}</span>
                  <span className="text-xs md:text-sm font-[JetBrains_Mono]">{formatCurrency(stats?.fix_total || 0)}</span>
                </div>
                <div className="h-2 md:h-3 bg-muted rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-[#E31E24] rounded-full transition-all duration-500"
                    style={{ 
                      width: `${stats?.year_total ? (stats.fix_total / stats.year_total * 100) : 0}%` 
                    }}
                  />
                </div>
              </div>
              <div>
                <div className="flex justify-between mb-2">
                  <span className="text-xs md:text-sm font-medium">{t("dashboard.variableCosts")}</span>
                  <span className="text-xs md:text-sm font-[JetBrains_Mono]">{formatCurrency(stats?.variable_total || 0)}</span>
                </div>
                <div className="h-2 md:h-3 bg-muted rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-[#09090b] rounded-full transition-all duration-500"
                    style={{ 
                      width: `${stats?.year_total ? (stats.variable_total / stats.year_total * 100) : 0}%` 
                    }}
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Payment Status: Open vs Paid */}
        <Card className="animate-fadeIn" data-testid="stat-payment-status">
          <CardHeader className="p-4 md:p-6">
            <CardTitle className="font-[Manrope] text-base md:text-lg">{t("dashboard.openExpenses")} vs. {t("dashboard.paidExpenses")}</CardTitle>
          </CardHeader>
          <CardContent className="p-4 md:p-6 pt-0">
            <div className="space-y-4">
              <div>
                <div className="flex justify-between mb-2">
                  <span className="text-xs md:text-sm font-medium flex items-center gap-1.5">
                    <Clock className="h-3.5 w-3.5 text-amber-500" />
                    {t("dashboard.openExpenses")} ({stats?.open_count || 0})
                  </span>
                  <span className="text-xs md:text-sm font-[JetBrains_Mono] text-amber-600">{formatCurrency(stats?.open_total || 0)}</span>
                </div>
                <div className="h-2 md:h-3 bg-muted rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-amber-500 rounded-full transition-all duration-500"
                    style={{ 
                      width: `${(stats?.open_total || 0) + (stats?.paid_total || 0) > 0 ? ((stats?.open_total || 0) / ((stats?.open_total || 0) + (stats?.paid_total || 0)) * 100) : 0}%` 
                    }}
                  />
                </div>
              </div>
              <div>
                <div className="flex justify-between mb-2">
                  <span className="text-xs md:text-sm font-medium flex items-center gap-1.5">
                    <CheckCircle2 className="h-3.5 w-3.5 text-green-500" />
                    {t("dashboard.paidExpenses")} ({stats?.paid_count || 0})
                  </span>
                  <span className="text-xs md:text-sm font-[JetBrains_Mono] text-green-600">{formatCurrency(stats?.paid_total || 0)}</span>
                </div>
                <div className="h-2 md:h-3 bg-muted rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-green-500 rounded-full transition-all duration-500"
                    style={{ 
                      width: `${(stats?.open_total || 0) + (stats?.paid_total || 0) > 0 ? ((stats?.paid_total || 0) / ((stats?.open_total || 0) + (stats?.paid_total || 0)) * 100) : 0}%` 
                    }}
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Top Categories */}
      <div className="grid grid-cols-1 gap-4 md:gap-6">
        <Card className="animate-fadeIn" data-testid="top-categories">
          <CardHeader className="p-4 md:p-6">
            <CardTitle className="font-[Manrope] text-base md:text-lg">{t("dashboard.byCategory")}</CardTitle>
          </CardHeader>
          <CardContent className="p-4 md:p-6 pt-0">
            <div className="space-y-2 md:space-y-3">
              {stats?.category_breakdown?.slice(0, 5).map((cat, index) => (
                <div key={cat.category_id} className="flex items-center justify-between py-1.5 md:py-2 border-b border-border last:border-0">
                  <div className="flex items-center gap-2 md:gap-3 min-w-0 flex-1">
                    <div 
                      className="h-2 w-2 md:h-3 md:w-3 rounded-full flex-shrink-0"
                      style={{ backgroundColor: CHART_COLORS[index % CHART_COLORS.length] }}
                    />
                    <span className="text-xs md:text-sm font-medium truncate">{cat.category_name}</span>
                  </div>
                  <div className="text-right flex-shrink-0 ml-2">
                    <span className="text-xs md:text-sm font-[JetBrains_Mono] font-medium">
                      {formatCurrency(cat.amount)}
                    </span>
                    <span className="hidden md:inline text-xs text-muted-foreground ml-2">
                      ({cat.count})
                    </span>
                  </div>
                </div>
              ))}
              {(!stats?.category_breakdown || stats.category_breakdown.length === 0) && (
                <div className="text-center text-muted-foreground py-4 text-sm">
                  {t("dashboard.noExpenses")}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
